import type { ActiveDataPoint, ActiveElement, Chart } from 'chart.js';

import { hideChartTooltip } from './tooltipDom';

export function getPointTimestamp(raw: unknown): number | null {
  if (!raw || typeof raw !== 'object' || !('x' in raw)) {
    return null;
  }

  const x = (raw as { x: number | Date | string }).x;
  if (x instanceof Date) {
    return x.getTime();
  }
  if (typeof x === 'number' && !Number.isNaN(x)) {
    return x;
  }
  if (typeof x === 'string') {
    const parsed = Date.parse(x);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

export function getHoverTimestampFromElements(
  chart: Chart<'line'>,
  elements: ActiveElement[]
): number | null {
  if (elements.length === 0) {
    return null;
  }

  const { datasetIndex, index } = elements[0];
  const raw = chart.data.datasets[datasetIndex]?.data?.[index];
  return getPointTimestamp(raw);
}

export function findClosestDataIndexForTimestamp(
  chart: Chart<'line'>,
  timestamp: number
): number | null {
  const firstDataset = chart.data.datasets[0]?.data;
  if (!firstDataset?.length) {
    return null;
  }

  let bestIndex: number | null = null;
  let bestDiff = Infinity;

  for (let i = 0; i < firstDataset.length; i++) {
    const pointTimestamp = getPointTimestamp(firstDataset[i]);
    if (pointTimestamp == null) {
      continue;
    }

    const diff = Math.abs(pointTimestamp - timestamp);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = i;
    }
  }

  return bestIndex;
}

function activeElementsForTimestamp(
  chart: Chart<'line'>,
  timestamp: number
): ActiveDataPoint[] | null {
  const index = findClosestDataIndexForTimestamp(chart, timestamp);
  if (index == null) {
    return null;
  }

  const activeElements = chart.data.datasets
    .map((_, datasetIndex) => ({ datasetIndex, index }))
    .filter(({ datasetIndex }) => !chart.getDatasetMeta(datasetIndex).hidden);

  return activeElements.length > 0 ? activeElements : null;
}

export function setActivePointsAtTimestamp(
  chart: Chart<'line'>,
  timestamp: number | null
): void {
  if (timestamp == null) {
    chart.setActiveElements([]);
    chart.draw();
    return;
  }

  const activeElements = activeElementsForTimestamp(chart, timestamp);
  if (activeElements == null) {
    chart.setActiveElements([]);
    chart.draw();
    return;
  }

  chart.setActiveElements(activeElements);
  chart.draw();
}

function clearChartTooltip(chart: Chart<'line'>, chartId: string): void {
  chart.setActiveElements([]);

  const tooltip = chart.tooltip;
  if (tooltip) {
    tooltip.setActiveElements([], { x: 0, y: 0 });
  }

  hideChartTooltip(chartId);
  chart.draw();
}

export function showTooltipAtTimestamp(
  chart: Chart<'line'>,
  timestamp: number | null,
  chartId: string
): void {
  if (timestamp == null) {
    clearChartTooltip(chart, chartId);
    return;
  }

  const tooltip = chart.tooltip;
  if (!tooltip) {
    hideChartTooltip(chartId);
    return;
  }

  const activeElements = activeElementsForTimestamp(chart, timestamp);
  if (activeElements == null) {
    clearChartTooltip(chart, chartId);
    return;
  }

  const xScale = chart.scales.x;
  if (!xScale) {
    clearChartTooltip(chart, chartId);
    return;
  }

  const pixelX = xScale.getPixelForValue(timestamp);
  const pixelY = chart.chartArea.top + 20;

  chart.setActiveElements(activeElements);
  tooltip.setActiveElements(activeElements, { x: pixelX, y: pixelY });
  chart.draw();
}
